import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { RefreshCw, Lock } from "lucide-react";
import { toast } from "sonner";

const STATUS_LABEL = {
  pendiente_responsable: "Pendiente de responsable", nueva: "Nueva", en_revision: "En revisión",
  en_seguimiento: "En seguimiento", cerrada: "Cerrada", archivada_historica: "Histórica archivada",
};
const COLS = Object.keys(STATUS_LABEL);
const fmt = (d) => (d ? new Date(d).toLocaleString("es-SV", { dateStyle: "short", timeStyle: "short" }) : "—");
const err = (e, m) => toast.error(e.response?.data?.detail?.[0]?.msg || e.response?.data?.detail || m);

const SummaryTable = ({ rows }) => (
  <Table data-testid="incidents-summary-table">
    <TableHeader><TableRow><TableHead>Colegio</TableHead><TableHead>Responsable</TableHead>{COLS.map((c) => <TableHead key={c}>{STATUS_LABEL[c]}</TableHead>)}</TableRow></TableHeader>
    <TableBody>
      {rows.map((r) => (
        <TableRow key={r.school_id} data-testid={`incidents-summary-row-${r.school_id}`}>
          <TableCell className="font-medium">{r.school_name}</TableCell>
          <TableCell>{r.has_valid_responsible ? <Badge className="bg-emerald-50 text-emerald-700">Activo</Badge> : <Badge className="bg-amber-50 text-amber-700">Falta</Badge>}</TableCell>
          {COLS.map((c) => <TableCell key={c}>{r.counts[c] || 0}</TableCell>)}
        </TableRow>
      ))}
      {rows.length === 0 && <TableRow><TableCell colSpan={COLS.length + 2} className="text-center text-gray-400 py-6">Sin incidencias registradas</TableCell></TableRow>}
    </TableBody>
  </Table>
);

const RequestForm = ({ schools, onDone }) => {
  const [schoolId, setSchoolId] = useState("");
  const [index, setIndex] = useState([]);
  const [incidentId, setIncidentId] = useState("");
  const [reason, setReason] = useState("");
  const [ref, setRef] = useState("");
  useEffect(() => {
    if (!schoolId) return;
    axios.get(`${API}/admin/incidents/index`, { params: { school_id: schoolId }, withCredentials: true })
      .then((r) => setIndex(r.data.incidents || [])).catch(() => setIndex([]));
  }, [schoolId]);
  const submit = async () => {
    try {
      await axios.post(`${API}/admin/incidents/break-glass`, { incident_id: incidentId, reason, procedure_reference: ref }, { withCredentials: true });
      toast.success("Solicitud registrada. Debe aprobarla otro superadministrador.");
      setIncidentId(""); setReason(""); setRef(""); onDone();
    } catch (e) { err(e, "No se pudo registrar la solicitud"); }
  };
  return (
    <div className="rounded-xl border border-gray-100 p-4 space-y-3" data-testid="break-glass-form">
      <h3 className="font-semibold text-gray-900">Solicitar acceso excepcional</h3>
      <div className="grid sm:grid-cols-2 gap-3">
        <Select value={schoolId} onValueChange={setSchoolId}>
          <SelectTrigger data-testid="break-glass-school-select"><SelectValue placeholder="Colegio" /></SelectTrigger>
          <SelectContent>{(schools || []).map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
        </Select>
        <Select value={incidentId} onValueChange={setIncidentId}>
          <SelectTrigger data-testid="break-glass-incident-select"><SelectValue placeholder="Incidencia (solo id y fecha)" /></SelectTrigger>
          <SelectContent>{index.map((i) => <SelectItem key={i.id} value={i.id}>{i.id.slice(0, 8)} · {fmt(i.created_at)} · {STATUS_LABEL[i.incident_status]}</SelectItem>)}</SelectContent>
        </Select>
      </div>
      <Input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="Referencia del procedimiento de emergencia documentado (p. ej. PE-SEG-2026-001)" data-testid="break-glass-reference-input" />
      <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Justificación detallada (mínimo 30 caracteres)" data-testid="break-glass-reason-input" />
      <Button onClick={submit} disabled={!incidentId || reason.trim().length < 30 || ref.trim().length < 3} data-testid="break-glass-submit-btn">Registrar solicitud</Button>
    </div>
  );
};

const RequestsTable = ({ items, currentUserId, onAction, onView }) => (
  <Table data-testid="break-glass-requests-table">
    <TableHeader><TableRow><TableHead>Solicitada</TableHead><TableHead>Por</TableHead><TableHead>Procedimiento</TableHead><TableHead>Estado</TableHead><TableHead>Expira</TableHead><TableHead /></TableRow></TableHeader>
    <TableBody>
      {items.map((r) => {
        const mine = r.requested_by === currentUserId;
        const live = r.status === "aprobada" && new Date(r.expires_at) > new Date();
        return (
          <TableRow key={r.id} data-testid={`break-glass-row-${r.id}`}>
            <TableCell className="text-xs">{fmt(r.requested_at)}</TableCell>
            <TableCell>{r.requested_by_name}</TableCell>
            <TableCell className="text-xs max-w-[260px]"><b>{r.procedure_reference}</b> · {r.reason}</TableCell>
            <TableCell><Badge variant="outline">{r.status}</Badge>{r.approved_by_name && <span className="block text-xs text-gray-400">por {r.approved_by_name}</span>}</TableCell>
            <TableCell className="text-xs">{fmt(r.expires_at)}</TableCell>
            <TableCell className="space-x-2 whitespace-nowrap">
              {r.status === "pendiente" && !mine && <>
                <Button size="sm" onClick={() => onAction(r.id, "approve")} data-testid={`break-glass-approve-${r.id}`}>Aprobar</Button>
                <Button size="sm" variant="outline" onClick={() => onAction(r.id, "reject")} data-testid={`break-glass-reject-${r.id}`}>Rechazar</Button>
              </>}
              {mine && live && <Button size="sm" variant="outline" onClick={() => onView(r.id)} data-testid={`break-glass-view-${r.id}`}>Ver (solo lectura)</Button>}
            </TableCell>
          </TableRow>
        );
      })}
      {items.length === 0 && <TableRow><TableCell colSpan={6} className="text-center text-gray-400 py-6">Sin solicitudes</TableCell></TableRow>}
    </TableBody>
  </Table>
);

const ReadOnlyDialog = ({ data, onClose }) => (
  <Dialog open={Boolean(data)} onOpenChange={(v) => !v && onClose()}>
    <DialogContent className="max-w-2xl bg-white" data-testid="break-glass-view-dialog">
      <DialogHeader>
        <DialogTitle>Acceso excepcional · solo lectura</DialogTitle>
        <DialogDescription>Este acceso queda registrado en la auditoría. Expira {fmt(data?.expires_at)}.</DialogDescription>
      </DialogHeader>
      {data && (
        <div className="space-y-2 text-sm">
          <p><b>{data.incident.student_name}</b> · grado {data.incident.grade} · {data.incident.category} · {data.incident.severity}</p>
          <p className="text-gray-500">{data.incident.archived_historical ? "Incidencia histórica archivada" : STATUS_LABEL[data.incident.incident_status]}</p>
          {data.incident.snippet && <p className="italic text-gray-600">{data.incident.snippet}</p>}
          {data.incident.student_message && <p className="whitespace-pre-wrap"><b>Estudiante:</b> {data.incident.student_message}</p>}
          {data.incident.tutor_response && <p className="whitespace-pre-wrap"><b>Tutor:</b> {data.incident.tutor_response}</p>}
        </div>
      )}
    </DialogContent>
  </Dialog>
);

const PauseControl = ({ pause, onDone }) => {
  const [reason, setReason] = useState("");
  const toggle = async () => {
    try {
      await axios.post(`${API}/admin/incidents/inbox-pause`, { paused: !pause, reason }, { withCredentials: true });
      toast.success(pause ? "Bandeja reanudada." : "Bandeja en pausa. El registro de incidencias continúa."); setReason(""); onDone();
    } catch (e) { err(e, "No se pudo cambiar el interruptor"); }
  };
  return (
    <div className={`rounded-xl border p-4 space-y-2 ${pause ? "border-red-200 bg-red-50" : "border-gray-100"}`} data-testid="inbox-pause-control">
      <h3 className="font-semibold text-gray-900">Interruptor de seguridad</h3>
      <p className="text-sm text-gray-600" data-testid="inbox-pause-status">
        {pause ? `Bandeja EN PAUSA desde ${fmt(pause.changed_at)} por ${pause.changed_by_name}: ${pause.reason}` : "Bandeja activa. La pausa suspende el acceso de los responsables sin detener el registro de incidencias."}
      </p>
      <Input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Motivo obligatorio (mínimo 10 caracteres)" data-testid="inbox-pause-reason-input" />
      <Button variant={pause ? "default" : "destructive"} onClick={toggle} disabled={reason.trim().length < 10} data-testid="inbox-pause-toggle-btn">{pause ? "Reanudar bandeja" : "Pausar bandeja"}</Button>
    </div>
  );
};

export const IncidentOversightSection = ({ schools, currentUserId }) => {
  const [rows, setRows] = useState([]);
  const [flags, setFlags] = useState({ break_glass_enabled: false, inbox_pause: null });
  const [reqs, setReqs] = useState([]);
  const [view, setView] = useState(null);
  const load = useCallback(async () => {
    try {
      const [s, r] = await Promise.all([
        axios.get(`${API}/admin/incidents/summary`, { withCredentials: true }),
        axios.get(`${API}/admin/incidents/break-glass`, { withCredentials: true }).catch(() => ({ data: { requests: [] } })),
      ]);
      setRows(s.data.schools || []); setReqs(r.data.requests || []);
      setFlags({ break_glass_enabled: Boolean(s.data.break_glass_enabled), inbox_pause: s.data.inbox_pause || null });
    } catch (e) { err(e, "Error al cargar incidencias"); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const act = async (id, action) => {
    try { await axios.post(`${API}/admin/incidents/break-glass/${id}/${action}`, {}, { withCredentials: true }); load(); }
    catch (e) { err(e, "No se pudo resolver la solicitud"); }
  };
  const open = async (id) => {
    try { const { data } = await axios.get(`${API}/admin/incidents/break-glass/${id}/view`, { withCredentials: true }); setView(data); }
    catch (e) { err(e, "Acceso no autorizado"); }
  };
  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-6 space-y-6" data-testid="incident-oversight-section">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="text-xl font-bold text-gray-900">Incidencias sensibles · solo cifras</h2>
          <p className="text-sm text-gray-500 flex items-center gap-1"><Lock className="w-3.5 h-3.5" />El contenido solo lo ve el responsable designado por cada colegio.{flags.break_glass_enabled ? " El acceso excepcional requiere la aprobación de otro superadministrador y expira en 60 minutos." : " El acceso excepcional está deshabilitado."}</p>
        </div>
        <Button variant="outline" onClick={load} className="gap-2" data-testid="incidents-reload-btn"><RefreshCw className="w-4 h-4" />Recargar</Button>
      </div>
      <SummaryTable rows={rows} />
      <PauseControl pause={flags.inbox_pause} onDone={load} />
      {flags.break_glass_enabled ? (<>
        <RequestForm schools={schools} onDone={load} />
        <RequestsTable items={reqs} currentUserId={currentUserId} onAction={act} onView={open} />
      </>) : (
        <p className="rounded-xl border border-gray-100 bg-gray-50 p-4 text-sm text-gray-600" data-testid="break-glass-disabled-notice">
          Acceso excepcional deshabilitado. Se habilitará cuando QHUB Global designe y autorice formalmente a un segundo superadministrador.
        </p>
      )}
      <ReadOnlyDialog data={view} onClose={() => setView(null)} />
    </div>
  );
};
